import { HtmlBlockPolicy } from '../consts/html-block-policy'
import {
  HtmlBlockHeightMessage,
  HtmlBlockPrintMessage,
  IHtmlBlockFrameRegistry,
} from '../utils/html-block/types'

type RegisteredFrame = {
  frame: HTMLIFrameElement
  onHeight: (height: number) => void
  lastRawHeight: number | null
}

const HTML_BLOCK_HEIGHT_OSCILLATION_GUARD_PX = 2

export class HtmlBlockFrameRegistry implements IHtmlBlockFrameRegistry {
  private readonly frames = new Map<string, RegisteredFrame>()
  private fallbackSequence = 0
  private listenerAttached = false

  constructor(
    private readonly win: Window,
    private readonly policy: HtmlBlockPolicy
  ) {}

  register(id: string, frame: HTMLIFrameElement, onHeight: (height: number) => void): () => void {
    this.frames.set(id, {
      frame,
      onHeight,
      lastRawHeight: null,
    })

    if (!this.listenerAttached) {
      this.win.addEventListener('message', this.handleMessage)
      this.listenerAttached = true
    }

    return () => {
      if (this.frames.get(id)?.frame !== frame) {
        return
      }

      this.frames.delete(id)
      if (this.frames.size === 0 && this.listenerAttached) {
        this.win.removeEventListener('message', this.handleMessage)
        this.listenerAttached = false
      }
    }
  }

  createFrameId(): string {
    try {
      const randomUUID = this.win.crypto?.randomUUID
      if (typeof randomUUID === 'function') {
        return `html-block-${randomUUID.call(this.win.crypto)}`
      }
    } catch {
      // Restricted browser contexts use the instance-local fallback.
    }

    this.fallbackSequence += 1
    return `html-block-fallback-${this.fallbackSequence}`
  }

  requestPrint(id: string): boolean {
    const registered = this.frames.get(id)
    const contentWindow = registered?.frame.contentWindow
    if (!contentWindow) {
      return false
    }

    const message: HtmlBlockPrintMessage = {
      source: this.policy.messageSource,
      id,
      action: 'print',
    }
    contentWindow.postMessage(message, '*')
    return true
  }

  size(): number {
    return this.frames.size
  }

  resetHeightTracking(id: string): void {
    const registered = this.frames.get(id)
    if (registered) {
      registered.lastRawHeight = null
    }
  }

  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    if (!event.data || typeof event.data !== 'object') {
      return
    }

    const data = event.data as Partial<HtmlBlockHeightMessage>
    if (
      data.source !== this.policy.messageSource ||
      typeof data.id !== 'string' ||
      typeof data.height !== 'number' ||
      !Number.isFinite(data.height)
    ) {
      return
    }

    const registered = this.frames.get(data.id)
    if (!registered || registered.frame.contentWindow !== event.source) {
      return
    }

    // The measured height is reported as-is. Clamping is a presentation concern of the
    // in-place frame: the overlay needs the real height so the modal body can be the only
    // scroll region.
    const rawHeight = Math.round(data.height)
    if (rawHeight < 0) {
      return
    }

    const previousRawHeight = registered.lastRawHeight
    if (
      previousRawHeight !== null &&
      Math.abs(rawHeight - previousRawHeight) < HTML_BLOCK_HEIGHT_OSCILLATION_GUARD_PX
    ) {
      return
    }

    registered.lastRawHeight = rawHeight
    registered.onHeight(rawHeight)
  }
}
